import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { TipTapEditor, MediaLibrary } from "../../components/admin";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format field keys to human-readable labels
const formatFieldLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const AdminPages = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [mediaInsertCallback, setMediaInsertCallback] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    page_type: "standard",
    content: "",
    meta_description: "",
    meta_keywords: "",
    is_published: false,
    fields: {},
  });

  const handleOpenMediaLibrary = (insertCallback) => {
    setMediaInsertCallback(() => insertCallback);
    setMediaLibraryOpen(true);
  };

  const handleMediaSelect = (url, position) => {
    if (mediaInsertCallback) mediaInsertCallback(url, position);
    setMediaLibraryOpen(false);
    setMediaInsertCallback(null);
  };

  const handleMediaClose = () => {
    setMediaLibraryOpen(false);
    setMediaInsertCallback(null);
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPages(res.data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  const handleEdit = (page) => {
    setCurrentPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      page_type: page.page_type,
      content: page.content,
      meta_description: page.meta_description || "",
      meta_keywords: page.meta_keywords || "",
      is_published: page.is_published,
      fields: page.fields || {},
    });
    setIsEditing(true);
  };

  const handleNew = () => {
    setCurrentPage(null);
    setFormData({
      title: "",
      slug: "",
      page_type: "standard",
      content: "",
      meta_description: "",
      meta_keywords: "",
      is_published: false,
      fields: {},
    });
    setIsEditing(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: currentPage ? formData.slug : generateSlug(title),
    });
  };

  const handleSave = async () => {
    try {
      if (currentPage) {
        await axios.put(`${API}/pages/${currentPage.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Page updated successfully");
      } else {
        await axios.post(`${API}/pages`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Page created successfully");
      }
      setIsEditing(false);
      fetchPages();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save page");
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;
    try {
      await axios.delete(`${API}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Page deleted");
      fetchPages();
    } catch (error) {
      toast.error("Failed to delete page");
    }
  };

  const togglePublish = async (page) => {
    try {
      await axios.put(
        `${API}/pages/${page.id}`,
        { is_published: !page.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPages();
      toast.success(page.is_published ? "Page unpublished" : "Page published");
    } catch (error) {
      toast.error("Failed to update page");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-pages">
        <div className="flex items-center justify-between">
          <h1 className="heading-3">Pages</h1>
          <button onClick={handleNew} className="btn-primary" data-testid="new-page-btn">
            <Plus size={18} className="mr-2" />
            New Page
          </button>
        </div>

        {/* Page List */}
        <div className="glass-card">
          {pages.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-8">No pages yet</p>
          ) : (
            <div className="space-y-3">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-white truncate">{page.title}</h3>
                      <span className="text-xs px-2 py-0.5 bg-its-teal-secondary/30 text-its-teal-light rounded">
                        {page.page_type}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          page.is_published
                            ? "bg-green-500/20 text-green-400"
                            : "bg-yellow-500/20 text-yellow-400"
                        }`}
                      >
                        {page.is_published ? "Published" : "Draft"}
                      </span>
                    </div>
                    <p className="text-xs text-its-offwhite/60">/{page.slug}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => togglePublish(page)}
                      className="p-2 text-its-offwhite/60 hover:text-white transition-colors"
                      title={page.is_published ? "Unpublish" : "Publish"}
                    >
                      {page.is_published ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      onClick={() => handleEdit(page)}
                      className="p-2 text-its-offwhite/60 hover:text-its-gold transition-colors"
                      data-testid={`edit-page-${page.id}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto bg-its-teal-dark border-its-teal-light/20"
            onInteractOutside={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle className="text-white">
                {currentPage ? "Edit Page" : "New Page"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="form-input"
                    data-testid="page-title-input"
                  />
                </div>
                <div>
                  <label className="form-label">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Page Type</label>
                <select
                  value={formData.page_type}
                  onChange={(e) => setFormData({ ...formData, page_type: e.target.value })}
                  className="form-input"
                >
                  <option value="standard">Standard</option>
                  <option value="landing">Landing Page</option>
                  <option value="about">About</option>
                  <option value="services">Services</option>
                  <option value="contact">Contact</option>
                  <option value="blog">Blog</option>
                  <option value="gallery">Gallery</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Meta Description</label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="form-input"
                    rows={2}
                    placeholder="SEO description..."
                  />
                </div>
                <div>
                  <label className="form-label">Meta Keywords</label>
                  <input
                    type="text"
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    className="form-input"
                    placeholder="keyword1, keyword2, ..."
                  />
                </div>
              </div>

              {/* Editable Fields (for hybrid pages like About, Services, Contact) */}
              {/* These render near the top/middle of the live page */}
              {formData.fields && Object.keys(formData.fields).length > 0 && (
                <div className="space-y-4 border-t border-its-teal-light/20 pt-4">
                  <h3 className="text-sm font-semibold text-its-gold uppercase tracking-wider">
                    Page Section Content
                  </h3>
                  <p className="text-xs text-its-offwhite/50">
                    Edit the text content for each section of this page. Changes appear on the live site immediately.
                  </p>
                  <div className="space-y-3">
                    {Object.entries(formData.fields).map(([key, value]) => {
                      const isLongText = key.includes("content") || key.includes("features") || key.includes("description") || key.includes("subtitle") || key.includes("text");
                      return (
                        <div key={key}>
                          <label className="form-label text-xs">{formatFieldLabel(key)}</label>
                          {key.includes("content") ? (
                            <TipTapEditor
                              content={value || ""}
                              onChange={(val) =>
                                setFormData({
                                  ...formData,
                                  fields: { ...formData.fields, [key]: val },
                                })
                              }
                              placeholder={`Edit ${formatFieldLabel(key)}...`}
                              onOpenMediaLibrary={handleOpenMediaLibrary}
                            />
                          ) : isLongText ? (
                            <textarea
                              value={value || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  fields: { ...formData.fields, [key]: e.target.value },
                                })
                              }
                              rows={key.includes("features") ? 6 : 2}
                              className="form-input text-sm"
                            />
                          ) : (
                            <input
                              type="text"
                              value={value || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  fields: { ...formData.fields, [key]: e.target.value },
                                })
                              }
                              className="form-input text-sm"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t border-its-teal-light/20 pt-4">
                <label className="form-label">Additional Content <span className="text-xs text-its-offwhite/40 font-normal">(displays at bottom of page)</span></label>
                <TipTapEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Write additional page content..."
                  onOpenMediaLibrary={handleOpenMediaLibrary}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4 rounded border-its-teal-light/30"
                  />
                  <span className="text-sm text-its-offwhite">Publish immediately</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleSave} className="btn-primary" data-testid="save-page-btn">
                  {currentPage ? "Update Page" : "Create Page"}
                </button>
                <button onClick={() => setIsEditing(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Media Library — rendered OUTSIDE Radix Dialog to avoid pointer-events/focus trap */}
        <MediaLibrary
          isOpen={mediaLibraryOpen}
          onClose={handleMediaClose}
          onSelect={handleMediaSelect}
          token={token}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
